import React, { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import NewMiniDrawer from "../../components/navMenu/menu-nav";
import driverService from "../../service/driver.service";
import freightService from "../../service/freight.service";
import pickupOrderApi from "../../service/modules/pickupOrder.api";
import ModernAlertDialog from "../frete/components/ModernAlertDialog";
import "./pickupOrder.css";

// Campos iniciais da Ordem de Coleta.
// Nesta primeira etapa a tela e apenas visual/administrativa,
// sem persistir dados e sem gerar PDF.
const INITIAL_FORM = {
  freightNumber: "",
  driverName: "",
  vehiclePlate: "",
  vehicleModel: "",
  trailerPlate: "",
  senderCnpj: "",
  senderName: "",
  senderCep: "",
  senderAddress: "",
  senderNumber: "",
  senderCity: "",
  senderState: "",
  receiverName: "",
  receiverCep: "",
  receiverAddress: "",
  receiverNumber: "",
  receiverCity: "",
  receiverState: "",
};

function onlyFilled(value) {
  return value === undefined || value === null ? "" : String(value);
}

function formatAddress(place) {
  if (!place) return "";

  return [
    place.address || place.logradouro,
    place.city,
    place.uf || place.state,
    place.cep,
  ]
    .filter(Boolean)
    .join(" - ");
}

function getDocumentValue(place) {
  return onlyFilled(
    place?.cpf_cnpj ||
      place?.cnpj ||
      place?.document ||
      place?.documentNumber ||
      place?.cpfCnpj,
  );
}

function normalizeStoppingPoint(stop, index) {
  return {
    id: onlyFilled(stop?.id),
    stopOrder: stop?.stop_order || index + 1,
    cnpj: getDocumentValue(stop),
    name: onlyFilled(stop?.name || stop?.razaoSocial || stop?.companyName),
    cep: onlyFilled(stop?.cep || stop?.zipcode),
    address: onlyFilled(stop?.logradouro || stop?.address || stop?.street),
    number: onlyFilled(stop?.number || stop?.numero),
    city: onlyFilled(stop?.city || stop?.cidade),
    state: onlyFilled(stop?.uf || stop?.state),
  };
}

function selectedNames(value) {
  const items = Array.isArray(value?.dados) ? value.dados : [];
  return items
    .filter((item) => item?.selected)
    .map((item) => item?.name)
    .filter(Boolean)
    .join(", ");
}

function normalizeText(value) {
  return String(value || "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function isBlank(value) {
  return !String(value || "").trim();
}

function cleanText(value) {
  return String(value || "").trim();
}

function onlyDigits(value, maxLength) {
  const digits = onlyFilled(value).replace(/\D/g, "");
  return typeof maxLength === "number" ? digits.slice(0, maxLength) : digits;
}

function formatCnpj(value) {
  const digits = onlyDigits(value, 14);

  if (!digits) {
    return onlyFilled(value);
  }

  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3/$4")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, "$1.$2.$3/$4-$5");
}

function formatCep(value) {
  const digits = onlyDigits(value, 8);

  if (!digits) {
    return onlyFilled(value);
  }

  return digits.replace(/^(\d{5})(\d)/, "$1-$2");
}

function getFirstStoppingPointId(stops) {
  const firstStop = Array.isArray(stops) ? stops.find((stop) => cleanText(stop?.id)) : null;
  return firstStop ? cleanText(firstStop.id) : "";
}

function isContractedFreight(freight) {
  const statusCode = String(freight?.status?.code || "").trim();
  const statusDescribe = normalizeText(
    freight?.status?.describe ||
      freight?.status?.description ||
      freight?.status?.described ||
      freight?.status,
  );

  return statusCode === "04" || statusDescribe === "contratado";
}

function PickupOrderField({ label, required, helperText, className = "", ...props }) {
  return (
    <label className={`pickupOrder-field ${className}`}>
      <span className="pickupOrder-fieldLabel">{label}</span>
      <TextField {...props} size="small" fullWidth helperText={helperText} />
    </label>
  );
}

function ReviewField({ label, value, min = 12, max = 72, className = "" }) {
  const text = onlyFilled(value) || "-";
  const width = `${Math.min(max, Math.max(min, text.length + 2))}ch`;

  return (
    <label className={`pickupOrder-review-field ${className}`} style={{ "--review-field-width": width }}>
      <span className="pickupOrder-fieldLabel">{label}</span>
      <span className="pickupOrder-review-value">{text}</span>
    </label>
  );
}

export default function PickUpOrder() {
  const usuarioLogado = useSelector((state) => state.usuarioLogado);
  const usuarioEmail = useSelector((state) => state.usuarioEmail);
  const user = useSelector((state) => state.user);
  const [form, setForm] = useState(INITIAL_FORM);
  const [stoppingPoints, setStoppingPoints] = useState([]);
  const [freightId, setFreightId] = useState("");
  const [freightUids, setFreightUids] = useState({
    uidShipper: "",
    uidDriver: "",
  });
  const [openReviewModal, setOpenReviewModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fetchingFreight, setFetchingFreight] = useState(false);
  const [dialogMessage, setDialogMessage] = useState({
    open: false,
    status: "success",
    title: "",
    message: "",
  });
  const freightSearchRef = useRef(0);

  function handleChange(field, value) {
    // Mantem cada campo editavel manualmente.
    // Depois este mesmo ponto pode receber o preenchimento automatico pelo frete.
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function handleStoppingPointChange(index, field, value) {
    setStoppingPoints((prev) =>
      prev.map((stop, stopIndex) =>
        stopIndex === index
          ? {
              ...stop,
              [field]: value,
            }
          : stop,
      ),
    );
  }

  function showDialogMessage(status, title, message) {
    setDialogMessage({
      open: true,
      status,
      title,
      message,
    });
  }

  function getMissingRequiredFields() {
    const missingFields = [];

    [
      { label: "Frete", value: form.freightNumber },
      { label: "Motorista", value: form.driverName },
      { label: "Veiculo", value: form.vehiclePlate },
      { label: "Modelo", value: form.vehicleModel },
    ].forEach((field) => {
      if (isBlank(field.value)) missingFields.push(field.label);
    });

    if (hasStoppingPoints) {
      stoppingPoints.forEach((stop, index) => {
        const stopLabel = `Parada ${stop.stopOrder || index + 1}`;

        [
          { label: `${stopLabel}: CNPJ`, value: stop.cnpj },
          { label: `${stopLabel}: Nome`, value: stop.name },
          { label: `${stopLabel}: CEP`, value: stop.cep },
          { label: `${stopLabel}: Endereco`, value: stop.address },
          { label: `${stopLabel}: Numero`, value: stop.number },
          { label: `${stopLabel}: Cidade`, value: stop.city },
          { label: `${stopLabel}: UF`, value: stop.state },
        ].forEach((field) => {
          if (isBlank(field.value)) missingFields.push(field.label);
        });
      });
    } else {
      [
        { label: "CNPJ", value: form.senderCnpj },
        { label: "Nome", value: form.senderName },
        { label: "CEP", value: form.senderCep },
        { label: "Endereco", value: form.senderAddress },
        { label: "Numero", value: form.senderNumber },
        { label: "Cidade", value: form.senderCity },
        { label: "UF", value: form.senderState },
      ].forEach((field) => {
        if (isBlank(field.value)) missingFields.push(field.label);
      });
    }

    return missingFields;
  }

  function handleOpenReviewModal() {
    const missingFields = getMissingRequiredFields();

    if (missingFields.length > 0) {
      showDialogMessage("warning", "Campos obrigatórios!", [
        "Alguns campos sao de preenchimento obrigatorio:",
        ...missingFields,
      ]);
      return;
    }

    setOpenReviewModal(true);
  }

  async function getFreightByNumber(freightNumber) {
    const searchValues = [freightNumber];

    if (/^\d+$/.test(freightNumber)) {
      const numericValue = Number(freightNumber);
      if (Number.isSafeInteger(numericValue)) {
        searchValues.push(numericValue);
      }
    }

    for (const value of searchValues) {
      const snapshot = await freightService.getFreightByIdContractNumber(value);
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data(),
        };
      }
    }

    return null;
  }

  async function getDriverData(uidDriver) {
    if (!uidDriver) {
      return {
        profile: null,
        documents: null,
      };
    }

    const [profileSnap, documentsSnap] = await Promise.all([
      driverService.getDriverAvailable(uidDriver),
      driverService.getDriverAvailableById(uidDriver).catch(() => null),
    ]);

    return {
      profile: profileSnap?.exists ? profileSnap.data() : null,
      documents: documentsSnap?.exists ? documentsSnap.data() : null,
    };
  }

  async function fillFormByFreight(freightNumber) {
    const requestId = freightSearchRef.current + 1;
    freightSearchRef.current = requestId;
    setFetchingFreight(true);

    try {
      const freight = await getFreightByNumber(freightNumber);

      if (requestId !== freightSearchRef.current) return;

      if (!freight) {
        setFreightId("");
        setFreightUids({
          uidShipper: "",
          uidDriver: "",
        });
        setStoppingPoints([]);
        showDialogMessage("warning", "Atenção!", "Frete não encontrado para o número informado.");
        return;
      }

      if (!isContractedFreight(freight)) {
        setFreightId("");
        setFreightUids({
          uidShipper: "",
          uidDriver: "",
        });
        setStoppingPoints([]);
        setForm((prev) => ({
          ...INITIAL_FORM,
          freightNumber: prev.freightNumber,
        }));
        showDialogMessage(
          "warning",
          "Atenção!",
          "Este frete não está em conformidade com os requisitos para a criação de uma Ordem de Coleta.",
        );
        return;
      }

      const uidDriver = freight?.freight?.getDriverFreight?.uidDriver || "";
      const [driverData, stoppingPointsSnap] = await Promise.all([
        getDriverData(uidDriver),
        freightService.getStoppingPointsByFreight(freight.id).catch(() => null),
      ]);
      const { profile, documents } = driverData;
      const driverVehicle = documents?.vehicle || {};
      const firstTrailer = Array.isArray(driverVehicle.reboques)
        ? driverVehicle.reboques.find((reboque) => reboque?.placaReboque)
        : null;
      const normalizedStops = stoppingPointsSnap?.docs
        ? stoppingPointsSnap.docs.map((doc, index) =>
            normalizeStoppingPoint(
              {
                id: doc.id,
                ...doc.data(),
              },
              index,
            ),
          )
        : [];
      const firstStop = normalizedStops[0];
      const lastStop = normalizedStops[normalizedStops.length - 1];

      if (requestId !== freightSearchRef.current) return;

      setFreightId(freight.id || "");
      setFreightUids({
        uidShipper: onlyFilled(freight?.shipper?.uid),
        uidDriver: onlyFilled(uidDriver),
      });
      setStoppingPoints(normalizedStops);
      setForm((prev) => ({
        ...prev,
        freightNumber,
        driverName:
          onlyFilled(profile?.name || profile?.fullName || freight?.freight?.getDriverFreight?.name) ||
          prev.driverName,
        vehiclePlate: onlyFilled(driverVehicle.vehiclePlate) || prev.vehiclePlate,
        vehicleModel:
          onlyFilled(driverVehicle.descricaoVeiculo || driverVehicle.vehicleType) ||
          selectedNames(freight?.vehicle?.typeVehicle) ||
          prev.vehicleModel,
        trailerPlate: onlyFilled(firstTrailer?.placaReboque) || prev.trailerPlate,
        senderCnpj:
          onlyFilled(firstStop?.cnpj) || getDocumentValue(freight?.firstDelivery) || prev.senderCnpj,
        senderName:
          onlyFilled(firstStop?.name) || onlyFilled(freight?.firstDelivery?.name) || prev.senderName,
        senderCep: onlyFilled(firstStop?.cep) || onlyFilled(freight?.firstDelivery?.cep) || prev.senderCep,
        senderAddress:
          onlyFilled(firstStop?.address) ||
          onlyFilled(freight?.firstDelivery?.address || freight?.firstDelivery?.logradouro) ||
          formatAddress(freight?.firstDelivery) ||
          prev.senderAddress,
        senderNumber:
          onlyFilled(firstStop?.number) ||
          onlyFilled(freight?.firstDelivery?.number || freight?.firstDelivery?.numero) ||
          prev.senderNumber,
        senderCity:
          onlyFilled(firstStop?.city) || onlyFilled(freight?.firstDelivery?.city) || prev.senderCity,
        senderState:
          onlyFilled(firstStop?.state) ||
          onlyFilled(freight?.firstDelivery?.uf || freight?.firstDelivery?.state) ||
          prev.senderState,
        receiverName:
          onlyFilled(lastStop?.name) || onlyFilled(freight?.lastDelivery?.name) || prev.receiverName,
        receiverCep: onlyFilled(lastStop?.cep) || onlyFilled(freight?.lastDelivery?.cep) || prev.receiverCep,
        receiverAddress:
          onlyFilled(lastStop?.address) ||
          onlyFilled(freight?.lastDelivery?.address || freight?.lastDelivery?.logradouro) ||
          formatAddress(freight?.lastDelivery) ||
          prev.receiverAddress,
        receiverNumber:
          onlyFilled(lastStop?.number) ||
          onlyFilled(freight?.lastDelivery?.number || freight?.lastDelivery?.numero) ||
          prev.receiverNumber,
        receiverCity:
          onlyFilled(lastStop?.city) || onlyFilled(freight?.lastDelivery?.city) || prev.receiverCity,
        receiverState:
          onlyFilled(lastStop?.state) ||
          onlyFilled(freight?.lastDelivery?.uf || freight?.lastDelivery?.state) ||
          prev.receiverState,
      }));

      showDialogMessage("success", "Muito bem!", "Dados do frete carregados com sucesso.");
    } catch (error) {
      console.error("Erro ao buscar dados do frete:", error);
      setFreightId("");
      setFreightUids({
        uidShipper: "",
        uidDriver: "",
      });
      setStoppingPoints([]);
      showDialogMessage("error", "Atenção!", "Não foi possivel buscar os dados do frete informado.");
    } finally {
      if (requestId === freightSearchRef.current) {
        setFetchingFreight(false);
      }
    }
  }

  useEffect(() => {
    const freightNumber = form.freightNumber.trim();

    if (!freightNumber) {
      setFreightId("");
      setFreightUids({
        uidShipper: "",
        uidDriver: "",
      });
      setStoppingPoints([]);
      setFetchingFreight(false);
      return undefined;
    }

    if (freightNumber.length < 3) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      fillFormByFreight(freightNumber);
    }, 700);

    return () => window.clearTimeout(timeoutId);
  }, [form.freightNumber]);

  function buildPayload() {
    const freightNumber = Number(form.freightNumber);
    const trailerPlate = form.trailerPlate.trim();
    const normalizedStoppingPoints = stoppingPoints.map((stop, index) => ({
      id: cleanText(stop.id),
      stoppingPointId: cleanText(stop.id),
      stopOrder: stop.stopOrder || index + 1,
      cnpj: cleanText(stop.cnpj),
      name: cleanText(stop.name),
      cep: cleanText(stop.cep),
      address: cleanText(stop.address),
      number: cleanText(stop.number),
      city: cleanText(stop.city),
      state: cleanText(stop.state),
    }));
    const firstStop = normalizedStoppingPoints[0];
    const lastStop = normalizedStoppingPoints[normalizedStoppingPoints.length - 1];
    const sender = firstStop || {
      cnpj: cleanText(form.senderCnpj),
      name: cleanText(form.senderName),
      cep: cleanText(form.senderCep),
      address: cleanText(form.senderAddress),
      number: cleanText(form.senderNumber),
      city: cleanText(form.senderCity),
      state: cleanText(form.senderState),
    };
    const recipient = lastStop || {
      cnpj: "",
      name: cleanText(form.receiverName),
      cep: cleanText(form.receiverCep),
      address: cleanText(form.receiverAddress),
      number: cleanText(form.receiverNumber),
      city: cleanText(form.receiverCity),
      state: cleanText(form.receiverState),
    };
    const stoppingPointId = getFirstStoppingPointId(stoppingPoints);
    const payload = {
      numberSerial: Number.isNaN(freightNumber) ? cleanText(form.freightNumber) : freightNumber,
      stoppingPointId: stoppingPointId || undefined,
      freightId: freightId || undefined,
      uidShipper: freightUids.uidShipper || undefined,
      uidDriver: freightUids.uidDriver || undefined,
      driver: cleanText(form.driverName),
      
      vehicle: cleanText(form.vehiclePlate),
      model: cleanText(form.vehicleModel),
      sender: {
        cnpj: sender.cnpj,
        name: sender.name,
        cep: sender.cep,
        address: sender.address,
        number: sender.number,
        city: sender.city,
        state: sender.state,
      },
      recipient: {
        cnpj: recipient.cnpj,
        name: recipient.name,
        cep: recipient.cep,
        address: recipient.address,
        number: recipient.number,
        city: recipient.city,
        state: recipient.state,
      },
      stoppingPoints: normalizedStoppingPoints,
      createdBy: {
        uid: user?.uid || "",
        name: user?.name || "",
        
      },
    };

    if (trailerPlate) {
      payload.hasTrailer = true;
      payload.trailer = {
        plate: trailerPlate,
      };
    }

    return payload;
  }

 async function handleConfirm() {
  setSaving(true);

  const payload = buildPayload();

  console.log("PAYLOAD ENVIADO:", payload);

  try {
    await pickupOrderApi.create(payload);

    setOpenReviewModal(false);
    showDialogMessage(
      "success",
      "Muito bem!",
      "Ordem de Coleta salva com sucesso."
    );
  } catch (error) {
    console.error("Erro ao enviar Ordem de Coleta:", error);
    console.log("ERRO DO BACKEND:", error?.response?.data);

    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data ||
      "Não foi possível enviar a Ordem de Coleta. Tente novamente.";

    showDialogMessage("error", "Atenção!", apiMessage);
  } finally {
    setSaving(false);
  }
}

  if (usuarioLogado <= 0) {
    return <Navigate to="/login" />;
  }

  const hasStoppingPoints = stoppingPoints.length > 0;

  return (
    <NewMiniDrawer
      divOpen={
        <div className="freight-content pickupOrder-page">
          <div className="container pickupOrder-container">
            <div className="pickupOrder-header">
              <div className="pickupOrder-titleGroup">
                <span className="pickupOrder-titleIcon" aria-hidden="true" />
                <Typography variant="h5" component="h1" className="pickupOrder-title">
                  Ordem de Coleta
                </Typography>
              </div>
              <span className="pickupOrder-countBadge">
                {hasStoppingPoints
                  ? `${stoppingPoints.length} parada${stoppingPoints.length === 1 ? "" : "s"}`
                  : "1 coleta"}
              </span>
            </div>

            <Paper className="pickupOrder-card" elevation={0}>
              <div className="pickupOrder-grid pickupOrder-grid-top">
                <PickupOrderField
                  label="Frete"
                  value={form.freightNumber}
                  onChange={(event) => handleChange("freightNumber", event.target.value)}
                  helperText={fetchingFreight ? "Buscando dados do frete..." : ""}
                />

                <PickupOrderField
                  label="Motorista"
                  value={form.driverName}
                  onChange={(event) => handleChange("driverName", event.target.value)}
                />
              </div>

              <div className="pickupOrder-grid pickupOrder-grid-vehicle">
                <PickupOrderField
                  label="Veiculo"
                  value={form.vehiclePlate}
                  onChange={(event) => handleChange("vehiclePlate", event.target.value)}
                />

                <PickupOrderField
                  label="Modelo"
                  value={form.vehicleModel}
                  onChange={(event) => handleChange("vehicleModel", event.target.value)}
                />

                <PickupOrderField
                  label="Reboque (opcional)"
                  value={form.trailerPlate}
                  onChange={(event) => handleChange("trailerPlate", event.target.value)}
                />
              </div>

              {hasStoppingPoints ? (
                <div className="pickupOrder-stops">
                  {stoppingPoints.map((stop, index) => (
                    <section className="pickupOrder-section" key={stop.id || stop.stopOrder || index}>
                      <div className="pickupOrder-stopHeader">
                        <div className="pickupOrder-sectionTitle">
                          Parada {stop.stopOrder || index + 1}
                        </div>
                      </div>

                      <div className="pickupOrder-grid pickupOrder-grid-person">
                        <PickupOrderField
                          label="CNPJ"
                          required
                          value={formatCnpj(stop.cnpj)}
                          onChange={(event) =>
                            handleStoppingPointChange(index, "cnpj", onlyDigits(event.target.value, 14))
                          }
                        />

                        <PickupOrderField
                          label="Nome"
                          required
                          value={stop.name}
                          onChange={(event) =>
                            handleStoppingPointChange(index, "name", event.target.value)
                          }
                        />
                      </div>

                      <div className="pickupOrder-grid pickupOrder-grid-address">
                        <PickupOrderField
                          label="CEP"
                          required
                          value={formatCep(stop.cep)}
                          onChange={(event) =>
                            handleStoppingPointChange(index, "cep", onlyDigits(event.target.value, 8))
                          }
                        />

                        <PickupOrderField
                          label="Endereco"
                          required
                          value={stop.address}
                          onChange={(event) =>
                            handleStoppingPointChange(index, "address", event.target.value)
                          }
                        />

                        <PickupOrderField
                          label="N.o"
                          required
                          value={stop.number}
                          onChange={(event) =>
                            handleStoppingPointChange(index, "number", event.target.value)
                          }
                        />

                        <PickupOrderField
                          label="Cidade"
                          required
                          value={stop.city}
                          onChange={(event) =>
                            handleStoppingPointChange(index, "city", event.target.value)
                          }
                        />

                        <PickupOrderField
                          label="Estado"
                          required
                          value={stop.state}
                          onChange={(event) =>
                            handleStoppingPointChange(index, "state", event.target.value)
                          }
                        />
                      </div>
                    </section>
                  ))}
                </div>
              ) : (
                <>
                  <section className="pickupOrder-section">
                    <div className="pickupOrder-sectionTitle pickupOrder-sectionTitle-chip">
                      <span className="pickupOrder-sectionChip">Coleta</span>
                    </div>
                    <div className="pickupOrder-grid pickupOrder-grid-person">
                      <PickupOrderField
                        label="CNPJ"
                        required
                        value={formatCnpj(form.senderCnpj)}
                        onChange={(event) => handleChange("senderCnpj", onlyDigits(event.target.value, 14))}
                      />

                      <PickupOrderField
                        label="Nome"
                        required
                        value={form.senderName}
                        onChange={(event) => handleChange("senderName", event.target.value)}
                      />
                    </div>

                    <div className="pickupOrder-grid pickupOrder-grid-address">
                      <PickupOrderField
                        label="CEP"
                        required
                        value={formatCep(form.senderCep)}
                        onChange={(event) => handleChange("senderCep", onlyDigits(event.target.value, 8))}
                      />

                      <PickupOrderField
                        label="Endereco"
                        required
                        value={form.senderAddress}
                        onChange={(event) => handleChange("senderAddress", event.target.value)}
                      />

                      <PickupOrderField
                        label="N.o"
                        required
                        value={form.senderNumber}
                        onChange={(event) => handleChange("senderNumber", event.target.value)}
                      />

                      <PickupOrderField
                        label="Cidade"
                        required
                        value={form.senderCity}
                        onChange={(event) => handleChange("senderCity", event.target.value)}
                      />

                      <PickupOrderField
                        label="Estado"
                        required
                        value={form.senderState}
                        onChange={(event) => handleChange("senderState", event.target.value)}
                      />
                    </div>
                  </section>

                  <section className="pickupOrder-section">
                    <div className="pickupOrder-sectionTitle pickupOrder-sectionTitle-chip">
                      <span className="pickupOrder-sectionChip">Entrega</span>
                    </div>
                    <div className="pickupOrder-grid pickupOrder-grid-person pickupOrder-grid-person-single">
                      <PickupOrderField
                        label="Destinatario"
                        value={form.receiverName}
                        onChange={(event) => handleChange("receiverName", event.target.value)}
                      />
                    </div>

                    <div className="pickupOrder-grid pickupOrder-grid-address">
                      <PickupOrderField
                        label="CEP"
                        value={formatCep(form.receiverCep)}
                        onChange={(event) => handleChange("receiverCep", onlyDigits(event.target.value, 8))}
                      />

                      <PickupOrderField
                        label="Endereco"
                        value={form.receiverAddress}
                        onChange={(event) => handleChange("receiverAddress", event.target.value)}
                      />

                      <PickupOrderField
                        label="N.o"
                        value={form.receiverNumber}
                        onChange={(event) => handleChange("receiverNumber", event.target.value)}
                      />

                      <PickupOrderField
                        label="Cidade"
                        value={form.receiverCity}
                        onChange={(event) => handleChange("receiverCity", event.target.value)}
                      />

                      <PickupOrderField
                        label="Estado"
                        value={form.receiverState}
                        onChange={(event) => handleChange("receiverState", event.target.value)}
                      />
                    </div>
                  </section>
                </>
              )}

              <div className="pickupOrder-actions">
                <Button variant="contained" onClick={handleOpenReviewModal}>
                  Salvar
                </Button>
              </div>
            </Paper>

            <Dialog
              open={openReviewModal}
              onClose={() => setOpenReviewModal(false)}
              maxWidth="md"
              fullWidth
              className="pickupOrder-review-dialog"
            >
              <DialogTitle className="pickupOrder-review-title">
                <span className="pickupOrder-review-icon">!</span>
                <strong>Revisar Ordem de Coleta</strong>
                <small>Confira as informacoes antes de confirmar:</small>
              </DialogTitle>

              <DialogContent className="pickupOrder-review-content">
                <div className="pickupOrder-review-form">
                  <div className="pickupOrder-review-row">
                    <ReviewField label="Frete" value={form.freightNumber} min={16} max={24} />
                    <ReviewField label="Motorista" value={form.driverName} min={18} max={42} />
                    <ReviewField label="Veiculo" value={form.vehiclePlate} min={14} max={20} />
                    <ReviewField label="Modelo" value={form.vehicleModel} min={14} max={28} />
                    <ReviewField
                      label="Reboque"
                      value={form.trailerPlate.trim() || "Sem reboque"}
                      min={16}
                      max={28}
                    />
                    <ReviewField
                      label="Paradas"
                      value={
                        hasStoppingPoints
                          ? `${stoppingPoints.length} parada${stoppingPoints.length === 1 ? "" : "s"}`
                          : "-"
                      }
                      min={14}
                      max={20}
                      className="pickupOrder-review-field-fit"
                    />
                  </div>

                  {hasStoppingPoints ? (
                    stoppingPoints.map((stop, index) => (
                      <section
                        className="pickupOrder-review-section"
                        key={stop.id || stop.stopOrder || index}
                      >
                        <div className="pickupOrder-sectionTitle">
                          Parada {stop.stopOrder || index + 1}
                        </div>

                        <div className="pickupOrder-review-row">
                          <ReviewField label="CNPJ" value={formatCnpj(stop.cnpj)} min={20} max={26} />
                          <ReviewField label="Nome" value={stop.name} min={28} max={80} />
                        </div>

                        <div className="pickupOrder-review-row">
                          <ReviewField label="CEP" value={formatCep(stop.cep)} min={14} max={18} />
                          <ReviewField label="Endereco" value={stop.address} min={34} max={78} />
                          <ReviewField label="N.o" value={stop.number} min={8} max={12} />
                          <ReviewField label="Cidade" value={stop.city} min={18} max={34} />
                          <ReviewField label="Estado" value={stop.state} min={8} max={10} />
                        </div>
                      </section>
                    ))
                  ) : (
                    <>
                      <section className="pickupOrder-review-section">
                        <div className="pickupOrder-sectionTitle">Coleta</div>
                        <div className="pickupOrder-review-row">
                          <ReviewField label="CNPJ" value={formatCnpj(form.senderCnpj)} min={20} max={26} />
                          <ReviewField label="Nome" value={form.senderName} min={28} max={80} />
                        </div>

                        <div className="pickupOrder-review-row">
                          <ReviewField label="CEP" value={formatCep(form.senderCep)} min={14} max={18} />
                          <ReviewField label="Endereco" value={form.senderAddress} min={34} max={78} />
                          <ReviewField label="N.o" value={form.senderNumber} min={8} max={12} />
                          <ReviewField label="Cidade" value={form.senderCity} min={18} max={34} />
                          <ReviewField label="Estado" value={form.senderState} min={8} max={10} />
                        </div>
                      </section>

                      <section className="pickupOrder-review-section">
                        <div className="pickupOrder-sectionTitle">Entrega</div>
                        <div className="pickupOrder-review-row">
                          <ReviewField label="Destinatario" value={form.receiverName} min={28} max={80} />
                        </div>

                        <div className="pickupOrder-review-row">
                          <ReviewField label="CEP" value={formatCep(form.receiverCep)} min={14} max={18} />
                          <ReviewField label="Endereco" value={form.receiverAddress} min={34} max={78} />
                          <ReviewField label="N.o" value={form.receiverNumber} min={8} max={12} />
                          <ReviewField label="Cidade" value={form.receiverCity} min={18} max={34} />
                          <ReviewField label="Estado" value={form.receiverState} min={8} max={10} />
                        </div>
                      </section>
                    </>
                  )}
                </div>
              </DialogContent>

              <DialogActions className="pickupOrder-review-actions">
                <Button
                  variant="contained"
                  onClick={() => setOpenReviewModal(false)}
                  disabled={saving}
                >
                  Voltar
                </Button>
                <Button variant="contained" onClick={handleConfirm} disabled={saving}>
                  {saving ? (
                    <CircularProgress className="pickupOrder-button-loader" size={18} />
                  ) : null}
                  Confirmar
                </Button>
              </DialogActions>
            </Dialog>

            <ModernAlertDialog
              open={dialogMessage.open}
              handleClose={() => setDialogMessage((prev) => ({ ...prev, open: false }))}
              title={dialogMessage.title}
              msg={dialogMessage.message}
              iconType={dialogMessage.status}
            />
          </div>
        </div>
      }
    />
  );
}
